import { useEffect, useState, useMemo } from "react";
import { TeamColor } from "../../TeamColor/TeamColor";
import { Competitor } from "../../Competitor/Competitor";
import cx from "components/src/utils/cx";
import { BoxScoreDetails } from "./BoxScoreDetails";
import { normalizeSport } from "shared/src/utils/sportMap";

import { getTeamInfo } from "shared/src/hooks/getTeamInfo";
import { makeSoccerAbbreviation } from "shared/src/utils/soccerAbbreviation";

const collegeScale = { scaleX: -1.3, scaleY: 1.3 };
const scale = { scale: 1.3 };

const secondaryLeagues = ["ncaam", "ncaaw", "collegebasketball", "wnba"];
const secondarySports = ["basketball"];

export const configureTeamColorVariant = ({
  team = "",
  team1 = "",
  team2 = "",
  sport: _sportRaw = "",
  competitionType,
  league: leagueProp = "",
}: {
  team?: string;
  team1?: string;
  team2?: string;
  sport: string;
  competitionType?: string;
  league?: string;
}) => {
  const sport = normalizeSport(_sportRaw) ?? _sportRaw;
  const limitTo = competitionType !== undefined || leagueProp 
    ? { limitTo: [competitionType !== undefined ? competitionType : leagueProp] } 
    : undefined;
  const teamMetaDataArr = [
    getTeamInfo(team, limitTo),
    getTeamInfo(team1, limitTo),
    getTeamInfo(team2, limitTo),
  ].filter((team) => !!team);
  const league = teamMetaDataArr?.at(0)?.league ?? "";

  const hasSecondary =
    secondaryLeagues.includes(league) ||
    sport === "football" ||
    (league === "college" && sport && secondarySports.includes(sport));

  if (!team && !team1 && !team2) {
    console.error(
      "CUI configureTeamColorVariant: Must supply a team { team: '' } or two teams, { team1: '', team2: ''}"
    );
  }
  if (!sport && league === "college") {
    console.error(
      "CUI configureTeamColorVariant: 'sport' prop is required to apply color variant for college teams"
    );
  }

  return hasSecondary ? "secondary" : "primary";
};

export const BoxScoreV2 = ({
  isVertical,
  isHorizontal,
  isPreGame,
  isLive,
  ariaLabelPreGame,
  ariaLabel,
  competitors,
  _id,
  variant,
  detailsProps,
  teams,
  breakpoint,
  hasLiveStream,
  secondaryEventDetailsSyncMapper,
  secondaryEventDetails,
  hideExtendedDetails,
  eventDetails,
  onClick,
  iconSize,
  _competition,
  scoreSize,
  _textSize,
  _competitorOne,
  _competitorTwo,
  _name,
  _showColorBars,
  animation,
  sport: _sportRaw,
  competitionType,
  setRef,
  width,
  height,
  patternOpacity = 0.4,
  rest,
  _hideScorePadding,
}) => {
  const sport = normalizeSport(_sportRaw) ?? _sportRaw;
  const [forcedLocalTheme, setForcedLocalTheme] = useState("czr-dark");
  const canTransform =
    _competition === "nfl" ||
    (_competition === "college" && sport === "americanfootball");
  const _competitorOneTransform = canTransform
    ? { ...scale, rotate: "16deg" }
    : {};
  let _competitorTwoTransform: React.CSSProperties = canTransform
    ? { rotate: "-16deg" }
    : {};
  _competitorTwoTransform =
    canTransform && _competition === "college"
      ? { ..._competitorTwoTransform, ...collegeScale }
      : _competition === "nfl"
        ? { ..._competitorTwoTransform, ...scale }
        : {};

  useEffect(() => {
    setTimeout(() => {
      const bodyTheme =
        typeof window !== "undefined" &&
        Array.from(document?.body?.classList)
          .filter((c) => /wh-|czr-|czr-palace-|horseshoe-/.test(c))
          ?.at(0);
      const forcedLocalTheme =
        bodyTheme && typeof bodyTheme === "string"
          ? bodyTheme.replace("-light", "-dark")
          : "czr-dark";
      setForcedLocalTheme(forcedLocalTheme);
    }, 0);
  }, []);

  const _teamColorVariant = useMemo(() => {
    return configureTeamColorVariant({
      team1: _competitorOne.team,
      team2: _competitorTwo.team,
      sport,
      competitionType,
    });
  }, [_competitorOne?.team, _competitorTwo?.team, sport, competitionType]);

  const _fades = useMemo(()=>{
    if (rest?.fades && Array.isArray(rest?.fades)) return rest?.fades;
    return [
        { preset: "centerFade", width: 300 },
        // { preset: "topFade", height: 30 },
        // { preset: "bottomFade", height: 30 },
      ]
  },[rest?.fades]);

  const _patterns = useMemo(()=>{
    if (rest?.patterns && Array.isArray(rest?.patterns)) return rest?.patterns;
    return [
        { preset: "reflectionLeading", opacity: patternOpacity, blendMode: "multiply" },
        { preset: "reflectionTrailing", opacity: patternOpacity, blendMode: "multiply" },
      ]
  },[rest?.patterns]);
  console.log({ _competitorOne, _competitorTwo});
  return (
    <TeamColor
      team1={_competitorOne.team}
      team2={_competitorTwo.team}
      teamColorVariant={_teamColorVariant}
      wrapperClasses={`cui__boxscore cui-min-h-[96px] cui-overflow-hidden cui-outline-none cui-select-none focus:cui-outline-none focus-visible:cui-ring-2 focus-visible:cui-ring-offset-1 focus-visible:cui-ring-fg-focus cui-flex cui-items-center cui-justify-around ${forcedLocalTheme}`}
      aria-label={isPreGame ? ariaLabelPreGame : ariaLabel}
      onClick={(e) => {
        onClick && onClick(competitors, e, _id);
      }}
      tabIndex={0}
      id={_id}
      {...rest}
      fades={_fades}
      patterns={_patterns}
      height={height}
    >
      <div
        className="cui-absolute cui-left-1/2 -cui-translate-x-1/2 cui-flex cui-justify-center cui-h-auto"
        // style={{ width: "calc(100% + 40px)" }}
      >
        <div className="cui-text-fg-global-white cui-w-full cui-h-full cui-min-h-20 cui-flex cui-items-center">
          <div className="cui-w-auto cui-flex-grow cui-flex cui-justify-end">
            <Competitor
              {..._competitorOne}
              compact
              inline
              force={_name}
              iconSize={96}
              textSize={_textSize}
              tabbable={false}
              scoreSize={scoreSize}
              sport={sport}
              competitionType={competitionType}
              hideScore={isPreGame}
              hasScorePadding
              flipIcon={
                _competition === "college" && sport === "baseball"
                  ? true
                  : false
              }
              hideScorePadding={_hideScorePadding}
              animation={animation}
              scoreStacked={isLive && sport !== "tennis"}
              responsiveName={true}
              textAlign="right"
              forceReadability={{ type: "onDark" }}
              logoType="helmet"
              mode="dark"
              transform={_competitorOneTransform}
              key={`cui-boxscore-competitor-1-${_id}-${breakpoint}`}
            />
          </div>
          <div
            className={cx({
              "cui-mx-md": isPreGame && sport !== "football",
            })}
          >
            <BoxScoreDetails {...detailsProps} showCountdown={false} />
          </div>
          <div className="cui-flex-grow">
            <Competitor
              {..._competitorTwo}
              compact
              inverse
              inline
              force={_name}
              iconSize={96}
              textSize={_textSize}
              tabbable={false}
              scoreSize={scoreSize}
              hideScore={isPreGame}
              responsiveName={true}
              sport={sport}
              competitionType={competitionType}
              hasScorePadding
              flipIcon={
                _competition === "college" && sport === "baseball"
                  ? false
                  : true
              }
              scoreStacked={isLive && sport !== "tennis"}
              hideScorePadding={_hideScorePadding}
              animation={animation}
              logoType="helmet"
              mode="dark"
              transform={_competitorTwoTransform}
              key={`cui-boxscore-competitor-2-${_id}-${breakpoint}`}
              forceReadability={{ type: "onDark" }}
            />
          </div>
        </div>
      </div>
    </TeamColor>
  );
};
